import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';
import { authService } from './auth.service';

// GraphQL subscription for all balances updates (multiple users)
export const ALL_BALANCES_UPDATED_SUBSCRIPTION = gql`
  subscription ($userIds: [Int!]!, $currentUserId: Int!, $from: String, $to: String) {
    allBalancesUpdated(userIds: $userIds, currentUserId: $currentUserId, from: $from, to: $to) {
      users {
        id
        username
        email
        phone
        credit
        isActive
        parentId
        comment
        lastLogin
        loginIp
        avatarUrl
        language
        timezone
        createdAt
        updatedAt
        totalActiveBonus
        wagerBonus
        dailyBonus
        hourlyBonus     
        balanceIn
        balanceOut
        profit
        hold
        holdPercentage
        parentIds
        matchedUserId
        Balance {
            id
            amount
            onHold
        }
      }
      matchedUserIds
      parentIds
    }
  }
`;

// Types for all balances subscription
export interface AllBalancesUpdatedUser {
  id: number;
  username: string;
  email?: string;
  phone?: string;
  credit: number;
  isActive: boolean;
  parentId?: number;
  comment?: string;
  lastLogin?: string;
  loginIp?: string;
  avatarUrl?: string;
  language?: string;
  timezone?: string;
  createdAt: string;
  updatedAt: string;
  totalActiveBonus?: number;
  wagerBonus?: number;
  dailyBonus?: number;
  hourlyBonus?: number;
  balanceIn?: number;
  balanceOut?: number;
  profit?: number;
  hold?: number;
  holdPercentage?: number;
  parentIds: number[];
  matchedUserId?: number;
  Balance?: {
    id: number;
    amount: number;
    onHold: number;
  };
}

export interface AllBalancesUpdatedData {
  users: AllBalancesUpdatedUser[];
  matchedUserIds: number[];
  parentIds: number[];
}

export interface AllBalancesSubscriptionVariables {
  userIds: number[];
  currentUserId: number;
  from?: string;
  to?: string;
}

export interface AllBalancesSubscriptionCallbacks {
  onUpdate?: (data: AllBalancesUpdatedData) => void;
  onError?: (error: any) => void;
  onComplete?: () => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

// All Balances Subscription service class
export class AllBalancesSubscriptionService {
  private static instance: AllBalancesSubscriptionService;
  private subscription: any = null;
  private isActive: boolean = false;
  private callbacks: AllBalancesSubscriptionCallbacks = {};
  private variables: AllBalancesSubscriptionVariables | null = null;

  private constructor() {}

  public static getInstance(): AllBalancesSubscriptionService {
    if (!AllBalancesSubscriptionService.instance) {
      AllBalancesSubscriptionService.instance = new AllBalancesSubscriptionService();
    }
    return AllBalancesSubscriptionService.instance;
  }

  /**
   * Start all balances subscription
   */
  async startSubscription(
    variables: AllBalancesSubscriptionVariables,
    callbacks?: AllBalancesSubscriptionCallbacks
  ): Promise<void> {
    try {
      // Get current user for validation
      const currentUser = authService.getStoredUser();
      if (!currentUser?.id) {
        throw new Error('No authenticated user found');
      }

      // Stop existing subscription if running
      if (this.isActive) {
        await this.stopSubscription();
      }

      // Store variables and callbacks
      this.variables = variables;
      this.callbacks = { ...callbacks };

      
      const subscriptionVariables = {
        userIds: variables.userIds,
        currentUserId: variables.currentUserId,
        from: variables.from,
        to: variables.to,
      };
      

      // Create subscription observable
      const subscriptionObservable = apolloClient.subscribe({
        query: ALL_BALANCES_UPDATED_SUBSCRIPTION,
        variables: subscriptionVariables,
      });
      // Subscribe to the observable
      this.subscription = subscriptionObservable.subscribe({
        next: (result) => {
          
          if (result.data?.allBalancesUpdated) {
            const updatedData = result.data.allBalancesUpdated as AllBalancesUpdatedData;
            
            // Dispatch custom event for components listening to balance updates
            window.dispatchEvent(new CustomEvent('allBalancesUpdated', {
              detail: updatedData
            }));

            // Call update callback
            if (this.callbacks.onUpdate) {
              this.callbacks.onUpdate(updatedData);
            }
          }
        },
        error: (error) => {
          this.isActive = false;
          
          // Call error callback
          if (this.callbacks.onError) {
            this.callbacks.onError(error);
          }
        },
        complete: () => {
          this.isActive = false;
          
          // Call complete callback
          if (this.callbacks.onComplete) {
            this.callbacks.onComplete();
          }
        }
      });

      this.isActive = true;

      // Call connect callback
      if (this.callbacks.onConnect) {
        this.callbacks.onConnect();
      }

    } catch (error) {
      throw error;
    }
  }

  /**
   * Stop the active all balances subscription
   */
  async stopSubscription(): Promise<void> {
    try {
      if (this.subscription) {
        this.subscription.unsubscribe();
        this.subscription = null;
      }
      
      this.isActive = false;
      this.callbacks = {};
      this.variables = null;
      

    } catch (error) {
      throw error;
    }
  }

  /**
   * Update subscription with new user IDs or filters
   */
  async updateSubscription(
    newVariables: Partial<AllBalancesSubscriptionVariables>
  ): Promise<void> {
    if (!this.variables) {
      throw new Error('No active subscription to update');
    }

    const updatedVariables = {
      ...this.variables,
      ...newVariables,
    };

    // Restart subscription with new variables
    const currentCallbacks = { ...this.callbacks };
    await this.stopSubscription();
    await this.startSubscription(updatedVariables, currentCallbacks);
  }

  /**
   * Add user IDs to existing subscription
   */
  async addUserIds(userIds: number[]): Promise<void> {
    if (!this.variables) {
      throw new Error('No active subscription to update');
    }

    const existingIds = this.variables.userIds || [];
    const newIds = Array.from(new Set([...existingIds, ...userIds])); // Remove duplicates

    await this.updateSubscription({ userIds: newIds });
  }

  /**
   * Remove user IDs from existing subscription
   */
  async removeUserIds(userIds: number[]): Promise<void> {
    if (!this.variables) {
      throw new Error('No active subscription to update');
    }

    const existingIds = this.variables.userIds || [];
    const filteredIds = existingIds.filter(id => !userIds.includes(id));

    await this.updateSubscription({ userIds: filteredIds });
  }

  /**
   * Check if subscription is currently active
   */
  isSubscriptionActive(): boolean {
    return this.isActive;
  }

  /**
   * Get current subscription variables
   */
  getCurrentVariables(): AllBalancesSubscriptionVariables | null {
    return this.variables;
  }

  /**
   * Restart subscription (useful for reconnection)
   */
  async restartSubscription(): Promise<void> {
    if (!this.variables) {
      throw new Error('No subscription variables to restart with');
    }

    const currentVariables = { ...this.variables };
    const currentCallbacks = { ...this.callbacks };
    await this.stopSubscription();
    await this.startSubscription(currentVariables, currentCallbacks);
  }

  /**
   * Update subscription callbacks
   */
  updateCallbacks(callbacks: AllBalancesSubscriptionCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }
}

// Export singleton instance
export const allBalancesSubscriptionService = AllBalancesSubscriptionService.getInstance();

// Convenience functions following your project pattern
export async function startAllBalancesSubscription(
  variables: AllBalancesSubscriptionVariables,
  callbacks?: AllBalancesSubscriptionCallbacks
): Promise<void> {
  return allBalancesSubscriptionService.startSubscription(variables, callbacks);
}

export async function stopAllBalancesSubscription(): Promise<void> {
  return allBalancesSubscriptionService.stopSubscription();
}

export function isAllBalancesSubscriptionActive(): boolean {
  return allBalancesSubscriptionService.isSubscriptionActive();
}

export async function updateAllBalancesSubscription(
  variables: Partial<AllBalancesSubscriptionVariables>
): Promise<void> {
  return allBalancesSubscriptionService.updateSubscription(variables);
}

export async function restartAllBalancesSubscription(): Promise<void> {
  return allBalancesSubscriptionService.restartSubscription();
}
